<script setup>
import { computed, ref, watch } from 'vue';
import IAcquirings from '@components/IAcquirings/IAcquirings.vue';
import IPromo from '@components/IPromo/IPromo.vue';
import VueCountdown from '@chenfengyuan/vue-countdown';
import Popper from 'vue3-popper';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import useCartStore from '@vueApp/store/modules/cart';
import IOrderItem from '@components/IOrderItem/IOrderItem.vue';
import CheckboxItem from '@vueApp/components/CheckboxItem.vue';
import { utils } from '@frontend/web/src/js/utils';

translator.getLanguages();

const cartStore = useCartStore();
const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});
const isAgreeWithRules = ref(false);
const isAgreeWithPersonalData = ref(false);

const subs = computed(() => props.order.cases.filter((caseItem) => caseItem.type === 'sub'));
const isSub = computed(() => subs.value.length > 0);
const rules = [
  { id: 1, link: 'https://www.hc-avto.ru/media/filebrowser/docs/%D0%9F%D1%80%D0%B0%D0%B2%D0%B8%D0%BB%D0%B0_%D0%9A%D0%A5%D0%9B_01_03.25.pdf', text: 'Rules of conduct for spectators,' },
  { id: 2, link: 'https://www.hc-avto.ru/tickets/buyrules/', text: 'Rules for purchasing tickets and season tickets and' },
  { id: 3, link: 'https://www.hc-avto.ru/tickets/refund/', text: 'Rules for the return of tickets and season tickets' },
];

function appliedPromo(orderData) {
  cartStore.updateOrderPrices(orderData);
}

const isAnyPushkinOrCinema = computed(() => props.order.acquirings.filter(
  (acquiring) => acquiring.payType === 'Pushkin' || acquiring.payType === 'Cinema',
));

const orderTimer = ref(props.order.paymentDeadline * 1000 - Date.now());
const showCartItem = ref(orderTimer.value > 0);
const cases = computed(() => props.order?.cases ?? []);
const parkingCases = computed(() => cases.value.filter((event) => event?.eventType === 'Парковка'));
const allHaveExtraInfo = computed(() => parkingCases.value.length > 0
    && parkingCases.value.every(
      (event) => Array.isArray(event.tickets)
        && event.tickets.length > 0
        && event.tickets.every((ticket) => ticket.extraInfo),
    ));

const isDisable = computed(
  () => !isAgreeWithRules.value
    || !isAgreeWithPersonalData.value
    || (parkingCases.value.length > 0 && !allHaveExtraInfo.value),
);

const isMobile = computed(() => window.innerWidth <= 991);

const tooltipText = computed(() => {
  if (parkingCases.value.length > 0 && !allHaveExtraInfo.value) {
    return isAgreeWithRules.value && isAgreeWithPersonalData.value
      ? translator.t('main', 'To continue shopping, please add your parking details')
      : translator.t('main', 'To continue shopping, please confirm your consent and add your parking details');
  }

  if (!isAgreeWithRules.value || !isAgreeWithPersonalData.value) {
    return isMobile.value
      ? translator.t('main', 'To continue, please confirm your consent')
      : translator.t('main', 'To continue purchasing, please confirm your consent');
  }
  return '';
});

function transformSingleNumberToTwoDigit(props) {
  const formattedProps = {};

  Object.entries(props).forEach(([key, value]) => {
    formattedProps[key] = value < 10 ? `0${value}` : String(value);
  });

  return formattedProps;
}

function deleteExpiredOrderById(orderId) {
  showCartItem.value = false;
  cartStore.deleteOrderByIdFromStore(orderId);
}

const isShowOrderLines = computed(
  () => props.order.sale || props.order.commission || isAnyPushkinOrCinema.value.length,
);
</script>

<template>
  <transition name="fade">
    <div v-if="showCartItem" class="order__item">
      <div class="order__header">
        <div class="order__info">
          <div class="order__info-number">{{ translator.t("main", "Order") }} №{{ order.id }}</div>
        </div>
        <vue-countdown
          v-slot="{ minutes, seconds }"
          class="order__timer"
          :time="orderTimer"
          :transform="transformSingleNumberToTwoDigit"
          @end="deleteExpiredOrderById(order.id)"
        >
          {{ translator.t("main", "Pay within") }}
          <span class="order__timer_red">{{ minutes }}:{{ seconds }}</span>
        </vue-countdown>
      </div>
      <div class="order__event">
        <IOrderItem
          v-for="itemCase in order.cases"
          :key="itemCase.id"
          :item-case="{ ...itemCase, orderId: order.id }"
          :trash-button="true"
          :is-history="false"
        />
      </div>
      <CheckboxItem
        class="order__agree-label"
        :checked="isAgreeWithRules"
        @change="isAgreeWithRules = !isAgreeWithRules"
      >
        <span>
          {{ translator.t('main', 'I agree with') }}
          <a
            v-for="rule in rules"
            :key="rule.id"
            :href="rule.link"
          >
            {{ translator.t('main', rule.text) }}
            <span />
          </a>
        </span> *
      </CheckboxItem>
      <CheckboxItem
        class="order__agree-label"
        :checked="isAgreeWithPersonalData"
        @change="isAgreeWithPersonalData = !isAgreeWithPersonalData"
      >
        <span>
          {{ translator.t('main', 'I agree to') }}
          <a href="https://www.hc-avto.ru/media/filebrowser/docs/agreements/%D0%A1%D0%BE%D0%B3%D0%BB%D0%B0%D1%81%D0%B8%D0%B5_%D0%BE%D1%82_17_07.2026.pdf">
            {{ translator.t('main', 'the processing of personal data') }}
          </a>
        </span> *
      </CheckboxItem>
      <div class="order__low">
        <i-promo
          v-if="!isSub"
          :order-id="order.id"
          :initial-code="order.promocode"
          class="order__promo"
          @applied="orderData => appliedPromo(orderData)"
        />
        <div v-show="isShowOrderLines" class="order__lines order__low-item">
          <div v-if="order.sale" class="order__line">
            <div class="order__prop">
              {{
                isSub
                  ? translator.t("main", "Discount for the purchase of a subscription")
                  : translator.t("main", "Discount by promocode")
              }}
              <Popper
                v-if="isSub"
                class="light order__sub-info-tooltip"
                placement="top"
                arrow
                hover
                :content="
                  translator.t(
                    'main',
                    'The discount amount depends on the number of events you choose.'
                  )
                "
              >
                <svg width="16" height="16" fill="none">
                  <use xlink:href="#question" />
                </svg>
              </Popper>
            </div>
            <div class="order__value">{{ order.sale }} <span class="ruble">₽</span></div>
          </div>

          <div v-if="order.commission" class="order__line">
            <div class="order__prop">{{ translator.t("main", "Commission") }}</div>
            <div class="order__value">{{ order.commission }} <span class="ruble">₽</span></div>
          </div>

          <div v-if="isAnyPushkinOrCinema.length" class="order__line">
            <div class="order__prop">{{ translator.t("main", "To pay") }}</div>
            <div class="order__value" style="font-size: 20px;">
              {{ utils.formatNumber(order.summaryPrice) }} ₽
            </div>
          </div>
        </div>
        <i-acquirings
          :order="order"
          :is-disable="isDisable"
          :tooltip-text="tooltipText"
        />
        <p v-if="isDisable" class="order__warning">
          {{ tooltipText }}
        </p>
      </div>
    </div>
  </transition>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";

:deep(.popper) {
  width: 280px;
  padding: 5px;

  border-radius: 6px;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.05);
  background-color: $white;

  color: $black;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
}

:deep(.popper #arrow::before) {
  transform: translateX(-5px) rotate(45deg);
  background: $white;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.tickets-item {
  margin-right: 16px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
}

@media (max-width: $screen-small) {
  .tickets-item {
    margin-right: 0px;
    width: 100%;
  }
}
</style>
